<script setup>
defineOptions({ name: "SegmentsPage" });
import { useSeo } from "@/composables/useSeo";
import { RouterLink } from "vue-router";
import CTA from "@/components/home/CTA.vue";
import SegmentHero from "@/components/ui/SegmentHero.vue";
import imageAcougue from "@/assets/segments/sem-watermark/acougue.webp";
import imageAutopecas from "@/assets/segments/sem-watermark/autopecas.webp";
import imageBijuterias from "@/assets/segments/sem-watermark/bijuterias.webp";
import imageCosmeticos from "@/assets/segments/sem-watermark/cosmeticos.webp";
import imageMaterialConstrucao from "@/assets/segments/sem-watermark/material-construcao.webp";
import imageMercados from "@/assets/segments/sem-watermark/mercados.webp";
import imageModa from "@/assets/segments/sem-watermark/moda.webp";
import imagePadarias from "@/assets/segments/sem-watermark/padarias.webp";
import imageSorveterias from "@/assets/segments/sem-watermark/sorveterias.webp";

useSeo({
  title: "Segmentos atendidos | Kali Gestão",
  description:
    "Conheça os principais segmentos atendidos pelo Kali Gestão: materiais de construção, moda, mercados, açougues, padarias e muito mais.",
  image: imageMaterialConstrucao,
});

const segments = [
  {
    name: "Material de Construção",
    description:
      "Controle de estoque, romaneios, notas fiscais e vendas no balcão.",
    to: "/segmento/material-de-construcao",
    badge: "Nova landing",
    image: imageMaterialConstrucao,
    imageAlt:
      "Segmento de materiais de construção: sistema para estoque, notas fiscais e vendas",
  },
  {
    name: "Moda, Roupas e Calçados",
    description:
      "Grade de produtos, promoções, PDV com PIX e vendas pelo WhatsApp.",
    to: "/segmento/moda",
    badge: "Nova landing",
    image: imageModa,
    imageAlt:
      "Segmento de moda: sistema para grade de produtos, promoções e vendas",
  },
  {
    name: "Mercados e Minimercados",
    description:
      "Frente de caixa rápida, estoque e controle financeiro integrado.",
    to: "/segmento/mercados",
    badge: "Nova landing",
    image: imageMercados,
    imageAlt:
      "Segmento de mercados: sistema para PDV, estoque e controle financeiro",
  },
  {
    name: "Açougues e Casas de Carne",
    description:
      "Controle de balança, cortes, validade e atendimento de balcão.",
    to: "/segmento/acougues",
    badge: "Nova landing",
    image: imageAcougue,
    imageAlt:
      "Segmento de açougues: sistema para balança, cortes e controle de validade",
  },
  {
    name: "Padarias e Confeitarias",
    description: "Gestão de produção, vitrines, consignados e vendas no caixa.",
    to: "/segmento/padarias",
    badge: "Nova landing",
    image: imagePadarias,
    imageAlt:
      "Segmento de padarias: sistema para produção, vitrine e vendas no caixa",
  },
  {
    name: "Lojas de Cosméticos e Perfumarias",
    description:
      "Estoque por marca, combos promocionais e vendas no PDV e online.",
    to: "/segmento/cosmeticos",
    badge: "Nova landing",
    image: imageCosmeticos,
    imageAlt:
      "Segmento de cosméticos: sistema para estoque por marca e combos promocionais",
  },
  {
    name: "Lojas de Autopeças e Oficinas Mecânicas",
    description:
      "Controle de estoque, OS e vendas integradas para autopeças e oficinas.",
    to: "/segmento/autopecas",
    badge: "Nova landing",
    image: imageAutopecas,
    imageAlt:
      "Segmento de autopeças: sistema para estoque, OS e vendas integradas",
  },
  {
    name: "Sorveterias e Açaiterias",
    description:
      "Controle de sabores, adicionais, estoque de insumos e frente de caixa.",
    to: "/segmento/sorveterias",
    badge: "Nova landing",
    image: imageSorveterias,
    imageAlt:
      "Segmento de sorveterias: sistema para sabores, adicionais e controle de insumos",
  },
  {
    name: "Lojas de Bijuterias e Acessórios",
    description:
      "Estoque por coleção, combos e vendas integradas no PDV e online.",
    to: "/segmento/bijuterias",
    badge: "Nova landing",
    image: imageBijuterias,
    imageAlt:
      "Segmento de bijuterias: sistema para estoque por coleção e vendas no PDV",
  },
];
</script>

<template>
  <div class="min-h-screen bg-white">
    <SegmentHero title="Segmentos atendidos pelo Kali Gestão"
      description="Confira alguns dos principais segmentos atendidos e acesse uma página com funcionalidades pensadas para o seu tipo de negócio."
      primary-label="Solicite uma demonstração gratuita" secondary-label="Ver soluções"
      secondary-href="/solucao/kali-gestao" :background-image="imageMercados"
      background-class="bg-slate-900 bg-cover bg-center" overlay-variant="left" />

    <section id="segmentos" class="py-16 bg-white scroll-mt-24">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="max-w-3xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Escolha seu segmento
          </h2>
          <p class="mt-4 text-gray-600 text-base sm:text-lg">
            Acesse uma página com as funcionalidades mais importantes para o seu
            tipo de negócio.
          </p>
        </div>

        <div class="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <RouterLink v-for="segment in segments" :key="segment.name" :to="segment.to"
            class="group overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:shadow-lg">
            <div class="relative h-40">
              <img :src="segment.image" :alt="segment.imageAlt" class="h-full w-full object-cover" loading="lazy" />
              <div class="absolute inset-0 bg-gradient-to-t from-slate-900/55 via-slate-900/10 to-transparent"></div>
              <span v-if="segment.badge"
                class="absolute left-4 top-4 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-kali-gold text-gray-900">
                {{ segment.badge }}
              </span>
              <div class="absolute bottom-4 left-4 right-4">
                <h3 class="text-lg font-extrabold text-white">
                  {{ segment.name }}
                </h3>
              </div>
            </div>
            <div class="p-5">
              <p class="text-base text-gray-600">
                {{ segment.description }}
              </p>
              <div
                class="mt-5 inline-flex items-center text-sm font-semibold text-[#006086] group-hover:text-[#004d6b]">
                Ver detalhes
                <svg class="ml-1.5 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </RouterLink>
        </div>
      </div>
    </section>

    <CTA />
  </div>
</template>
